import querystring from 'querystring';
import { EventType, ProductType, RouteName } from '@/shared/enums';
import fetchPlayerProduct from '@/shared/utils/fetch-player-product';

const DEEPLINK_REGEX = /^\/deeplink/;
const DEEPLINK_ACTION_REGEX = /^\/deeplink\/action\//;
const ADD = '/add';
const DETAIL = 'detail';
const CATALOG = 'catalog';
const WATCH = 'watch';
const PASSWORD_RESET = 'password/reset';
const ALLOWED_DEEPLINK_ACTIONS = [
  EventType.CHANGE_LANGUAGE,
  EventType.OPEN_PREFERENCE_CENTER,
];

const isType = (link) => {
  return (type) => {
    return link.includes(type);
  };
};

const createLink = ({ deeplink, callback, type }) => {
  let [newDeeplink, query] = deeplink.slice().split('?');

  if (type) {
    newDeeplink = newDeeplink
      .replace(`/${type}`, '')
      .split(/[/, ]+/)
      .filter(Boolean);
  }

  query = querystring.parse(query);

  return callback(newDeeplink, query);
};

const createDefaultLink = (link) => {
  const path = link.replace('tv', 'live_tv');
  return { path };
};

const createDetailLink = (link, query) => {
  const [type, slug, id, articleSlug, articleId] = link;

  const replaceType = {
    vod: 'movies',
    serial: 'series',
    live: 'live_tv',
  };
  const params = {
    label: replaceType[type],
    slug,
    type,
    id,
  };

  if (!(articleSlug && articleId)) {
    return {
      name: RouteName.DETAIL,
      params,
      query,
    };
  }
  const detailLinkHasType = isType(link);
  const [episode, programme] = [ProductType.EPISODE, ProductType.PROGRAMME].map((type) => type.toLowerCase());

  if (detailLinkHasType(episode)) {
    return createDetailParamsWithArticleSlug(episode);
  }
  return createDetailParamsWithArticleSlug(programme);

  function createDetailParamsWithArticleSlug(articleType) {
    return {
      name: RouteName.DETAIL_WITH_ARTICLE,
      params: {
        ...params,
        articleSlug,
        articleType,
        articleId,
      },
      query,
    };
  }
};

const createCatalogLink = (link) => {
  const [label, slug, id] = link;

  if (link.length > 1) {
    return {
      name: RouteName.CATALOG_WITH_CATEGORY,
      params: {
        label,
        slug,
        id,
      },
    };
  }

  return {
    name: RouteName.CATALOG,
    params: {
      label,
    },
  };
};

const createAddDeviceLink = (link) => {
  const path = link.replace(ADD, '/subscriber/connect-tv');
  return { path };
};

const createResetPasswordLink = (link, query) => {
  return {
    name: RouteName.SUBSCRIBER_PASSWORD_RESET,
    query,
  };
};

const createWatchLink = async ({ link, liveManager }) => {
  const watchLink = link.map((word) => {
    if (word === 'program') {
      return 'programme';
    }
    return word;
  });
  const [articleType, articleSlug, articleId] = watchLink;
  const standardLabels = {
    clip: 'clip',
    live: 'live_tv',
    serial: 'series',
    vod: 'movie',
  };

  if (standardLabels[articleType]) {
    return {
      name: RouteName.DETAIL,
      params: {
        label: standardLabels[articleType],
        slug: articleSlug,
        type: articleType,
        id: articleId,
      },
    };
  }

  const extendedLabels = {
    episode: 'series',
    programme: 'programme',
  };

  const { product } = await fetchPlayerProduct({ articleId, articleType, liveManager });
  const resource = product.channelId != null
    ? liveManager.channels.get(product.channelId)
    : product.season.serial;
  const { slug, type, id } = resource;

  return {
    name: RouteName.DETAIL_WITH_ARTICLE,
    params: {
      label: extendedLabels[articleType],
      slug,
      type: type.toLowerCase(),
      id,
      articleSlug,
      articleType,
      articleId,
    },
  };
};

export const transformDeeplinkToRoute = function ({ path, liveManager }) {
  const deeplink = path.replace(DEEPLINK_REGEX, '');
  const deeplinkHasType = isType(deeplink);

  if (deeplinkHasType(DETAIL)) {
    return createLink({
      deeplink,
      callback: createDetailLink,
      type: DETAIL,
    });
  }

  if (deeplinkHasType(CATALOG)) {
    return createLink({
      deeplink,
      callback: createCatalogLink,
      type: CATALOG,
    });
  }

  if (deeplinkHasType(ADD)) {
    return createLink({
      deeplink,
      callback: createAddDeviceLink,
    });
  }

  if (deeplinkHasType(WATCH)) {
    return createLink({
      deeplink,
      callback: (link) => createWatchLink({ link, liveManager }),
      type: WATCH,
    });
  }

  if (deeplinkHasType(PASSWORD_RESET)) {
    return createLink({
      deeplink,
      callback: createResetPasswordLink,
    });
  }

  return createLink({
    deeplink,
    callback: createDefaultLink,
  });
};

export const transformDeeplinkToAction = function ({ path, EventBus }) {
  const [actionName, actionParams] = path.replace(DEEPLINK_ACTION_REGEX, '').split('/');
  if (ALLOWED_DEEPLINK_ACTIONS.includes(actionName)) {
    EventBus.emit(actionName, actionParams);
  }
};
